import React from "react";

import AlertIcon from "@uui/AlertCircle";
import WarningIcon from "@uui/AlertTriangle";
import SuccessIcon from "@uui/CheckCircle";
import InfoIcon from "@uui/InfoCircle";

import { AlertProps, Theme, ThemeOptions } from "@mui/material";

const FILLED_SEVERITY_BG_COLOR = {
  error: () => "#D32F2F",
  warning: (theme: Theme) => theme.palette.warning.light,
  success: (theme: Theme) => theme.palette.success.main,
  info: (theme: Theme) => theme.palette.shades.secondary[500],
};
const OUTLINED_SEVERITY_BG_COLOR = {
  error: (theme: Theme) => theme.palette.shades.negative[50],
  warning: (theme: Theme) => theme.palette.shades.orange[50],
  success: (theme: Theme) => theme.palette.shades.positive[50],
  info: (theme: Theme) => theme.palette.shades.secondary[50],
};

const OUTLINED_SEVERITY_BORDER_COLOR = FILLED_SEVERITY_BG_COLOR;

const SEVERITY_ICON: Record<
  NonNullable<AlertProps["color"]>,
  React.ReactNode
> = {
  error: <AlertIcon />,
  warning: <WarningIcon />,
  success: <SuccessIcon />,
  info: <InfoIcon />,
};

const getBackgroundColor = (props: AlertProps, theme: Theme) => {
  const { variant, severity } = props;
  if (variant === "filled") {
    return FILLED_SEVERITY_BG_COLOR[severity ?? "info"](theme);
  } else if (variant === "outlined") {
    return OUTLINED_SEVERITY_BG_COLOR[severity ?? "info"](theme);
  }
  return undefined;
};

const getColor = (props: AlertProps, theme: Theme) => {
  const { variant } = props;
  if (variant === "filled") {
    return "#FFF";
  } else if (variant === "outlined") {
    return theme.palette.shades.neutral[700];
  }
  return undefined;
};

const getActionColor = (props: AlertProps, theme: Theme) => {
  const { variant, severity = "info" } = props;
  if (variant === "filled") {
    return "#FFF";
  } else if (variant === "outlined") {
    return OUTLINED_SEVERITY_BORDER_COLOR[severity](theme);
  }
  return undefined;
};

const components: ThemeOptions["components"] = {
  MuiAlert: {
    defaultProps: { iconMapping: SEVERITY_ICON },
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        backgroundColor: getBackgroundColor(ownerState, theme),
        color: getColor(ownerState, theme),
        fontFamily: theme.typography.fontFamilySecondary,
        fontWeight: 400,
        fontSize: "0.875rem",
        lineHeight: "18px",
        minHeight: ownerState.variant === "outlined" ? "42px" : undefined,
        padding:
          ownerState.variant === "outlined" ? "8px 10px 4px 10px" : "8px 10px",
        borderColor:
          ownerState.variant === "outlined"
            ? OUTLINED_SEVERITY_BORDER_COLOR[ownerState.severity ?? "info"](
                theme
              )
            : undefined,
        "& .MuiAlertTitle-root": {
          ...(ownerState.variant === "outlined"
            ? {
                fontFamily: theme.typography.fontFamily,
                color: theme.palette.shades.neutral[800],
                fontWeight: 500,
                fontSize: "1rem",
                lineHeight: "20px",
                letterSpacing: "-0.02rem",
                marginBottom: "4px",
                marginTop: 0,
              }
            : {}),
        },
      }),
      message: {
        display: "flex",
        alignItems: "center",
        padding: "0px",
      },
      icon: {
        padding: "2px 0",
        "& > svg": {
          width: "20px",
          height: "20px",
        },
      },
      action: ({ ownerState, theme }) => ({
        marginRight: ownerState.variant === "filled" ? "-2px" : undefined,
        alignSelf: ownerState.variant === "outlined" ? "flex-start" : undefined,
        padding: ownerState.variant === "outlined" ? "0 0 0 8px" : "0 0 0 16px",
        "& > .MuiButton-root": {
          color: getActionColor(ownerState, theme),
          fontWeight: 600,
        },
        "& > .MuiIconButton-root": {
          marginTop: ownerState.variant === "outlined" ? "-5px" : undefined,
          color:
            ownerState.variant === "outlined"
              ? theme.palette.shades.neutral[500]
              : undefined,
          "& svg": {
            width: "16px",
            height: "16px",
          },
        },
      }),
    },
  },
  MuiTooltip: {
    styleOverrides: {
      arrow: ({ theme }) => ({
        color: theme.palette.overlay.primary[4],
      }),
      tooltip: ({ theme }) => ({
        backgroundColor: theme.palette.overlay.primary[4],
        fontFamily: "Inter, sans-serif",
        textAlign: "center",
        maxWidth: 220,
        padding: "8px",
        color: "white",
        border: 0,
      }),
    },
  },
  MuiLink: {
    defaultProps: {
      underline: "none",
    },
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.link.main,
        fontWeight: 500,
      }),
    },
  },
};

export default components;
